import React, { useCallback, useEffect, useState } from "react";
import { IoMdAdd } from "react-icons/io";
import { skuApi } from "../../api/lab";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../AuthProvider";
import { useApp } from "../../AppProvider";
import { Page, Button, Badge, Table, Pager } from "../../Components/UI/ui";
import { GATE, SKU_KINDS, money, cap } from "../../lib/lab";
import SkuForm from "./SkuForm";

// Test catalogue & price list (§2). Admin+ edits, owner deletes.
const Catalogue = () => {
  const { can } = useAuth();
  const { toast, invalidateSkus, templates, loadTemplates } = useApp();
  const navigate = useNavigate();
  const [serviceType, setServiceType] = useState("");
  const [kind, setKind] = useState("");
  const [category, setCategory] = useState("");
  const [active, setActive] = useState("true");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ data: [], total: 0, totalPages: 1 });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(null);

  const canWrite = can(...GATE.catalogueWrite);
  const canDelete = can(...GATE.catalogueDelete);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (serviceType) params.serviceType = serviceType;
      if (kind) params.kind = kind;
      if (category) params.category = category;
      if (active) params.isActive = active;
      if (search.trim()) params.search = search.trim();
      setData(await skuApi.list(params));
    } catch (e) {
      toast.error(e);
    } finally {
      setLoading(false);
    }
  }, [page, serviceType, kind, category, active, search, toast]);

  const loadCategories = useCallback(async () => {
    try { setCategories(await skuApi.categories()); } catch { /* non-blocking */ }
  }, []);

  useEffect(() => {
    const h = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(h);
  }, [load, search]);
  useEffect(() => { loadCategories(); loadTemplates(); }, [loadCategories, loadTemplates]);
  useEffect(() => { setPage(1); }, [serviceType, kind, category, active, search]);

  const toggle = async (row) => {
    setBusy(row._id);
    try {
      await skuApi.toggle(row._id);
      invalidateSkus();
      toast.success(`${row.name} ${row.isActive ? "deactivated" : "activated"}`);
      load();
    } catch (e) { toast.error(e); } finally { setBusy(null); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}" permanently? Prefer deactivating unless it was created by mistake.`)) return;
    setBusy(row._id);
    try {
      await skuApi.remove(row._id);
      invalidateSkus();
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e); } finally { setBusy(null); }
  };

  const taxPct = (t) => (t?.cgst || 0) + (t?.sgst || 0) + (t?.igst || 0);

  const columns = [
    { key: "labSkuId", title: "Code", render: (r) => <span className="sub">{r.labSkuId}</span> },
    { key: "name", title: "Name", render: (r) => (<><div className="primary">{r.name}</div><div className="sub">{r.category || "—"}{r.kind === "scan" && r.modality ? ` · ${r.modality}` : ""}</div></>) },
    { key: "kind", title: "Kind", render: (r) => r.kind === "package"
        ? <button type="button" className="lab-link" onClick={(e) => { e.stopPropagation(); navigate("/packages"); }}>Package · {(r.templateIds || []).length} tests</button>
        : cap(r.kind) },
    { key: "serviceType", title: "Service", render: (r) => <Badge kind={r.serviceType}>{cap(r.serviceType)}</Badge> },
    { key: "templateIds", title: "Result sheet", render: (r) => {
        if (r.serviceType !== "pathology" || r.kind === "service") return <span className="sub">—</span>;
        const ids = (r.templateIds || []).map(String);
        if (ids.length === 0) return <Badge kind="unpaid">No definition</Badge>;
        const names = ids.map((id) => templates[id]?.shortName || templates[id]?.name || "?");
        return <span title={ids.map((id) => templates[id]?.name || id).join(", ")}>{names.slice(0, 3).join(", ")}{names.length > 3 ? ` +${names.length - 3}` : ""}</span>;
      } },
    { key: "unitPrice", title: "Price", align: "right", render: (r) => money(r.unitPrice) },
    { key: "tax", title: "Tax", align: "right", render: (r) => `${taxPct(r.tax)}%` },
    { key: "turnaroundHours", title: "TAT", render: (r) => (r.turnaroundHours ? `${r.turnaroundHours}h` : "—") },
    { key: "home", title: "Home", render: (r) => (r.homeCollectionAvailable ? <Badge kind="home">Yes</Badge> : <span className="sub">—</span>) },
    { key: "isActive", title: "Status", render: (r) => <Badge kind={r.isActive ? "on" : "off"}>{r.isActive ? "Active" : "Inactive"}</Badge> },
    ...(canWrite ? [{
      key: "actions", title: "", render: (r) => (
        <div className="lab-row end" onClick={(e) => e.stopPropagation()}>
          <Button size="xs" onClick={() => { setEditing(r); setFormOpen(true); }}>Edit</Button>
          <Button size="xs" loading={busy === r._id} onClick={() => toggle(r)}>{r.isActive ? "Deactivate" : "Activate"}</Button>
          {canDelete && <Button size="xs" variant="danger" onClick={() => remove(r)}>Delete</Button>}
        </div>
      ),
    }] : []),
  ];

  return (
    <Page
      title="Catalogue"
      subtitle="Tests, panels, scans and their prices"
      actions={canWrite && <Button variant="primary" onClick={() => { setEditing(null); setFormOpen(true); }}><IoMdAdd /> New item</Button>}
    >
      <div className="lab-toolbar">
        <input className="lab-input grow" placeholder="Search by name" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="lab-select" value={serviceType} onChange={(e) => setServiceType(e.target.value)}>
          <option value="">All services</option>
          <option value="pathology">Pathology</option>
          <option value="radiology">Radiology</option>
        </select>
        <select className="lab-select" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">All kinds</option>
          {SKU_KINDS.map((k) => <option key={k} value={k}>{cap(k)}</option>)}
        </select>
        <select className="lab-select" value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="lab-select" value={active} onChange={(e) => setActive(e.target.value)}>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
          <option value="">All</option>
        </select>
      </div>

      <div className="lab-page__body">
        <Table columns={columns} rows={data.data || []} loading={loading} onRowClick={canWrite ? (r) => { setEditing(r); setFormOpen(true); } : undefined} empty="No catalogue items yet" />
      </div>
      <Pager page={page} totalPages={data.totalPages} total={data.total} onChange={setPage} />

      <SkuForm open={formOpen} onClose={() => setFormOpen(false)} sku={editing} categories={categories} onSaved={() => { load(); loadCategories(); }} />
    </Page>
  );
};

export default Catalogue;
